import { useEffect, useState } from 'react';
import { code128, trazar } from '../lib/code128';
import { matrizQr, type Correccion, type MatrizQr } from '../lib/qr';
import type { ElementoEtiqueta, Orientacion, PlantillaEtiqueta } from '../api';

/**
 * Dibuja una etiqueta.
 *
 * Es el mismo dibujo para el editor, la vista previa y la impresión: si se
 * vieran distinto, lo que se aprueba en pantalla no sería lo que sale del
 * rollo. Se dibuja en SVG **con el milímetro como unidad**, así que lo que dice
 * la plantilla es literalmente lo que se dibuja, sin conversiones que puedan
 * quedar desfasadas.
 *
 * Todo sale en negro sobre blanco a propósito: la impresora es térmica y no
 * tiene con qué hacer otra cosa. Diseñar con colores sería prometer algo que
 * después no se cumple.
 */

const VARIABLE = /\{\{\s*([a-z][a-z0-9]*(?:\.[a-z][a-z0-9]*)?)\s*\}\}/gi;

/** Reemplaza las variables por sus valores; sin dato, queda vacío. */
export function resolver(texto: string, datos: Record<string, string>): string {
  return texto.replace(VARIABLE, (_, clave: string) => datos[clave.toLowerCase()] ?? '');
}

/**
 * Las variables que usa una plantilla, sin repetir. Sirve para saber qué hay
 * que preguntar antes de imprimir: si la etiqueta no lleva el dato, pedirlo
 * sería pedir algo que después no se dibuja en ninguna parte.
 */
export function variablesDe(plantilla: PlantillaEtiqueta): string[] {
  const usadas = new Set<string>();
  for (const e of plantilla.elementos) {
    for (const m of (e.contenido ?? '').matchAll(VARIABLE)) usadas.add(m[1]!.toLowerCase());
  }
  return [...usadas];
}

const TIPOGRAFIA = 'Geist, Helvetica, Arial, sans-serif';

function Texto({ e, valor }: { e: ElementoEtiqueta; valor: string }) {
  const cuerpo = e.cuerpo ?? 4;
  const lineas = valor.split('\n');
  const x = e.alineacion === 'centro' ? e.x + e.ancho / 2 : e.alineacion === 'derecha' ? e.x + e.ancho : e.x;
  const anclaje = e.alineacion === 'centro' ? 'middle' : e.alineacion === 'derecha' ? 'end' : 'start';

  return (
    <text
      x={x}
      y={e.y + cuerpo * 0.82}
      fontFamily={TIPOGRAFIA}
      fontSize={cuerpo}
      fontWeight={e.negrita ? 700 : 400}
      textAnchor={anclaje}
      fill="#000"
    >
      {lineas.map((linea, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : cuerpo * 1.2}>
          {linea}
        </tspan>
      ))}
    </text>
  );
}

function Barras({ e, valor }: { e: ElementoEtiqueta; valor: string }) {
  const codigo = code128(valor);
  const conTexto = e.mostrarTexto !== false;
  const altoTexto = conTexto ? Math.min(3.5, e.alto * 0.3) : 0;
  const altoBarras = Math.max(e.alto - altoTexto, 0.5);

  if (!codigo) {
    return (
      <g>
        <rect x={e.x} y={e.y} width={e.ancho} height={e.alto} fill="none" stroke="#000" strokeDasharray="1 1" />
        <text x={e.x + e.ancho / 2} y={e.y + e.alto / 2} fontSize={2.5} textAnchor="middle" fill="#000">
          código no válido
        </text>
      </g>
    );
  }

  return (
    <g>
      {/* `crispEdges` apaga el suavizado: si el navegador dibuja los bordes
          en gris, con barras finas el lector no engancha nada. Va de la mano
          con que `trazar()` deje cada borde en un punto entero de la
          impresora: ahí redondear al punto más cercano no mueve nada. */}
      {trazar(codigo, e.ancho).barras.map((b, i) => (
        <rect
          key={i}
          x={e.x + b.x}
          y={e.y}
          width={b.ancho}
          height={altoBarras}
          fill="#000"
          shapeRendering="crispEdges"
        />
      ))}
      {conTexto && (
        <text
          x={e.x + e.ancho / 2}
          y={e.y + e.alto}
          fontFamily={TIPOGRAFIA}
          fontSize={altoTexto * 0.85}
          textAnchor="middle"
          fill="#000"
        >
          {valor}
        </text>
      )}
    </g>
  );
}

function Qr({ e, valor }: { e: ElementoEtiqueta; valor: string }) {
  const [matriz, setMatriz] = useState<MatrizQr | null>(null);

  useEffect(() => {
    let vigente = true;
    void matrizQr(valor, (e.correccion as Correccion) ?? 'M').then((m) => {
      if (vigente) setMatriz(m);
    });
    return () => {
      vigente = false;
    };
  }, [valor, e.correccion]);

  // El QR es cuadrado: se usa el lado menor para no deformarlo.
  const lado = Math.min(e.ancho, e.alto);

  if (!matriz) {
    return <rect x={e.x} y={e.y} width={lado} height={lado} fill="none" stroke="#000" strokeDasharray="1 1" />;
  }

  const modulo = lado / matriz.length;
  return (
    <g>
      {matriz.map((fila, y) =>
        fila.map((prendido, x) =>
          prendido ? (
            <rect
              key={`${x},${y}`}
              x={e.x + x * modulo}
              y={e.y + y * modulo}
              // Un pelo de más para que no queden hilos blancos entre módulos
              // al rasterizar.
              width={modulo * 1.02}
              height={modulo * 1.02}
              fill="#000"
            />
          ) : null,
        ),
      )}
    </g>
  );
}

function Dibujo({ e, datos }: { e: ElementoEtiqueta; datos: Record<string, string> }) {
  switch (e.tipo) {
    case 'texto':
      return <Texto e={e} valor={resolver(e.contenido ?? '', datos)} />;
    case 'barras':
      return <Barras e={e} valor={resolver(e.contenido ?? '', datos)} />;
    case 'qr':
      return <Qr e={e} valor={resolver(e.contenido ?? '', datos)} />;
    case 'linea':
      return <rect x={e.x} y={e.y} width={e.ancho} height={Math.max(e.grosor ?? 0.4, 0.1)} fill="#000" />;
    case 'rectangulo':
      return (
        <rect
          x={e.x}
          y={e.y}
          width={e.ancho}
          height={e.alto}
          fill={e.relleno ? '#000' : 'none'}
          stroke="#000"
          strokeWidth={e.grosor ?? 0.4}
        />
      );
    case 'logo':
      return <image href="/logo.png" x={e.x} y={e.y} width={e.ancho} height={e.alto} preserveAspectRatio="xMidYMid meet" />;
    default:
      return null;
  }
}

export function ElementoDibujado({ e, datos }: { e: ElementoEtiqueta; datos: Record<string, string> }) {
  const contenido = <Dibujo e={e} datos={datos} />;
  if (!e.giro) return contenido;

  return <g transform={`rotate(${e.giro} ${e.x + e.ancho / 2} ${e.y + e.alto / 2})`}>{contenido}</g>;
}

export default function Etiqueta({
  plantilla,
  ancho,
  alto,
  orientacion = 'vertical',
  datos,
  className,
  style,
}: {
  plantilla: PlantillaEtiqueta;
  /** Medidas del **papel**, en milímetros: lo que se carga en la impresora. */
  ancho: number;
  alto: number;
  orientacion?: Orientacion;
  datos: Record<string, string>;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      viewBox={`0 0 ${ancho} ${alto}`}
      width={`${ancho}mm`}
      height={`${alto}mm`}
      className={className}
      style={style}
      role="img"
      aria-label="Etiqueta"
    >
      <rect x={0} y={0} width={ancho} height={alto} fill="#fff" />

      {/* Apaisada, el dibujo está hecho a lo largo y se apoya girado sobre el
          papel. El papel no cambia: sigue saliendo del mismo rollo. */}
      <g transform={orientacion === 'apaisada' ? `translate(${ancho} 0) rotate(90)` : undefined}>
        {plantilla.elementos.map((e) => (
          <ElementoDibujado key={e.id} e={e} datos={datos} />
        ))}
      </g>
    </svg>
  );
}
